import { CampusSummary } from "../../../../services/schools/campus.types";

export interface CampusDraft {
  name: string;
  abbreviation: string;
  cct: string;
}

export const emptyCampusDraft: CampusDraft = {
  name: "",
  abbreviation: "",
  cct: "",
};

const normalize = (value: string) => value.trim().toLowerCase();

/**
 * La raíz siempre primera, el resto alfabético. El backend ya devuelve este
 * orden; se reordena igual acá porque la tabla no puede depender de que un
 * endpoint mantenga su `sort` para que "Principal" quede arriba.
 */
export const sortCampuses = (campuses: CampusSummary[]): CampusSummary[] =>
  [...campuses].sort((a, b) => {
    if (a.is_main !== b.is_main) return a.is_main ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

/**
 * Abreviatura ya usada por otro plantel del árbol. El backend responde 409,
 * pero validarlo local evita el viaje en el caso feliz y deja el mensaje en
 * el campo en vez de en una notificación.
 */
export const isAbbreviationTaken = (
  campuses: CampusSummary[],
  abbreviation: string,
  excludeCampusId?: number
): boolean => {
  const candidate = normalize(abbreviation);
  if (!candidate) return false;
  return campuses.some(
    (campus) =>
      campus.id !== excludeCampusId &&
      normalize(campus.abbreviation ?? "") === candidate
  );
};

/** Mensaje de error del paso "Datos", o null si el borrador es válido. */
export const validateCampusDraft = (
  draft: CampusDraft,
  campuses: CampusSummary[],
  excludeCampusId?: number
): string | null => {
  if (!draft.name.trim()) return "El nombre del plantel es obligatorio.";
  if (!draft.abbreviation.trim()) return "La abreviatura es obligatoria.";
  if (isAbbreviationTaken(campuses, draft.abbreviation, excludeCampusId)) {
    return "Ya hay un plantel con esa abreviatura en esta escuela.";
  }
  return null;
};

/**
 * Body de create/update: el CCT vacío se OMITE en vez de mandarse como "".
 * Un plantel sin CCT propio guarda NULL, y el backend rechaza un string vacío.
 */
export const buildCampusPayload = (draft: CampusDraft) => ({
  name: draft.name.trim(),
  abbreviation: draft.abbreviation.trim(),
  ...(draft.cct.trim() ? { cct: draft.cct.trim() } : {}),
});
